import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithGoogle, checkUserAuthorization, updateUserLastLogin, createOrUpdateUserWithGoogleData } from '../firebase/auth';
import { Button } from '../components/ui';
import { AlertCircle, Loader2 } from 'lucide-react';
import gradientBg from '../assets/images/Gradient.avif';
import scoutLogo from '../assets/images/Scout Logo.png';

export function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from the location state
  const from = location.state?.from?.pathname || '/';

  const handleUserCheck = async (email: string, firebaseUser?: any) => {
    const normalizedEmail = email.toLowerCase();

    setIsLoading(true);
    setError(null);

    try {
      const { authorized, user, error: authError } = await checkUserAuthorization(normalizedEmail);
      
      if (!authorized) {
        setError(authError || 'Access denied');
        return;
      }

      // Update last login time only
      await updateUserLastLogin(normalizedEmail);

      // Optionally update basic profile fields (no role/assignment changes)
      if (firebaseUser && user) {
        await createOrUpdateUserWithGoogleData(firebaseUser);
      }

      // Redirect to the intended destination or based on role
      if (from === '/admin' && user?.role !== 'admin') {
        // If trying to access admin but not admin, redirect to home
        navigate('/');
      } else {
        // Redirect to intended destination or default based on role
        navigate(from !== '/login' ? from : (user?.role === 'admin' ? '/admin' : '/'));
      }
    } catch (err) {
      console.error('Error checking user authorization:', err);
      setError('An error occurred while checking your access. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { user, error } = await signInWithGoogle();
      
      if (error) {
        setError(error);
        return;
      }

      if (user?.email) {
        await handleUserCheck(user.email, user);
      }
    } catch (err) {
      console.error('Error signing in with Google:', err);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background image with opacity - same as main app */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${gradientBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.4
        }}
      />
      
      {/* Subtle grid pattern - same as main app */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.05)_1px,_transparent_0)] bg-[length:32px_32px] pointer-events-none" />
      
      {/* Login Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Main Login Card */}
          <div className="relative">
            {/* Card Background with enhanced styling */}
            <div className="bg-white/90 backdrop-blur-2xl rounded-3xl border border-white/50 shadow-2xl p-8 relative overflow-hidden">
              {/* Subtle inner glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent rounded-3xl" />
              
              {/* Content */}
              <div className="relative z-10">
                {/* Header with enhanced branding */}
                <div className="text-center mb-10">
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    <div className="relative">
                      <img 
                        src={scoutLogo} 
                        alt="Scout Logo" 
                        className="w-20 h-20 object-contain drop-shadow-lg"
                      />
                      {/* Logo glow effect */}
                      <div className="absolute inset-0 w-20 h-20 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-full blur-xl -z-10" />
                    </div>
                    <div className="text-left">
                      <h1 className="text-3xl font-display font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent tracking-tight leading-none">
                        Scout AI
                      </h1>
                      <p className="text-sm font-text font-semibold text-gray-500 tracking-wider uppercase">
                        Project Overview
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h2 className="text-2xl font-display font-bold text-gray-800">
                      Access Dashboard
                    </h2>
                    <p className="text-gray-600 font-text leading-relaxed">
                      Sign in to manage and track project progress.
                    </p>
                  </div>
                </div>

                {/* Error Message with enhanced styling */}
                {error && (
                  <div className="mb-8 p-4 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl flex items-center space-x-3 shadow-sm">
                    <div className="flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <span className="text-sm text-red-700 font-text font-medium">{error}</span>
                  </div>
                )}

                {/* Enhanced Sign In Button */}
                <div className="space-y-6">
                  <Button
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="w-full bg-[#fc5e58] hover:bg-[#e53e3a] text-white border-0 transition-all duration-300 py-4 px-8 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transform"
                  >
                    {isLoading ? (
                      <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                    ) : null}
                    {isLoading ? 'Signing in...' : 'Sign in with Strawberry SSO'}
                  </Button>
                  

                </div>

                {/* Enhanced Footer */}
                <div className="mt-10 pt-6 border-t border-gray-200/50">
                  <div className="text-center space-y-3">
                    <p className="text-sm text-gray-500 font-text leading-relaxed">
                      Only authorized users can access this system.
                    </p>
                    <p className="text-xs text-gray-400 font-text">
                      Contact your administrator if you need access
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-pink-400/20 to-purple-400/20 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
